<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { Sparkles } from 'lucide-vue-next'

import Menu from 'primevue/menu'
import AppButton from '@/components/atoms/buttons/AppButton.vue'
import AppSelect from '@/components/atoms/selects/AppSelect.vue'
import AppSelectButton from '@/components/atoms/buttons/AppSelectButton.vue'
import AppTextarea from '@/components/atoms/textarea/AppTextarea.vue'
import { useIAService } from '@/services/ia/useIAService'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  engine: {
    type: String,
    default: 'mistral',
  },
  appendMode: {
    type: String as () => 'replace' | 'append',
    default: 'replace',
  },
  buttonTitle: {
    type: String,
    default: '',
  },
  currentText: {
    type: String,
    default: '',
  },
  embedded: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'insert', value: string): void
}>()
const { t } = useI18n()
const effectiveButtonTitle = computed(() => props.buttonTitle || t('ai.assistant'))

const menuRef = ref()
const isGenerating = ref(false)
const prompt = ref('')
const result = ref('')
const controller = ref<AbortController | null>(null)
const activeTab = ref<'generate' | 'translate'>('generate')

const translateSource = ref('')
const translateTargetLanguage = ref('en')
const translateResult = ref('')
const isTranslating = ref(false)
const iaService = useIAService()

watch(isGenerating, (value) => {
  if (!value) {
    controller.value?.abort()
    controller.value = null
  }
})

onBeforeUnmount(() => {
  controller.value?.abort()
})

async function generate() {
  if (!prompt.value.trim() || isGenerating.value) return
  isGenerating.value = true
  result.value = ''
  controller.value = new AbortController()

  try {
    const stream = await iaService.generateStream(
      {
        prompt: prompt.value,
        engine: props.engine,
      },
      controller.value.signal,
    )

    const reader = stream.getReader()
    const decoder = new TextDecoder()
    let done = false
    while (!done) {
      const { value, done: readerDone } = await reader.read()
      done = readerDone
      if (value) {
        const chunk = decoder.decode(value, { stream: !done })
        result.value += chunk
      }
    }
  } catch (error) {
    if (!(error instanceof DOMException && error.name === 'AbortError')) {
      result.value = result.value || t('ai.errors.generate_failed')
    }
  } finally {
    isGenerating.value = false
  }
}

function stop() {
  controller.value?.abort()
  isGenerating.value = false
}

function insertGenerated() {
  emit('insert', result.value)
  menuRef.value.hide()
}

function insertTranslated() {
  emit('insert', translateResult.value)
  menuRef.value.hide()
}

function toggle(event: Event) {
  translateSource.value = props.currentText || translateSource.value
  menuRef.value.toggle(event)
}

async function translate() {
  if (!translateSource.value.trim() || isTranslating.value) return
  isTranslating.value = true
  translateResult.value = ''
  try {
    translateResult.value = await iaService.translate({
      text: translateSource.value,
      target_language: translateTargetLanguage.value,
      engine: props.engine,
    })
  } catch {
    translateResult.value = translateResult.value || t('ai.errors.translate_failed')
  } finally {
    isTranslating.value = false
  }
}

const languageOptions = computed(() => [
  { name: t('ai.languages.english'), value: 'en' },
  { name: t('ai.languages.spanish'), value: 'es' },
  { name: t('ai.languages.portuguese'), value: 'pt' },
  { name: t('ai.languages.french'), value: 'fr' },
  { name: t('ai.languages.german'), value: 'de' },
  { name: t('ai.languages.italian'), value: 'it' },
])

const tabOptions = computed(() => [
  { name: t('ai.tabs.generate'), value: 'generate' },
  { name: t('ai.tabs.translate'), value: 'translate' },
])
</script>

<template>
  <div v-if="!embedded" class="relative inline-flex items-center">
    <AppButton
      type="button"
      :title="effectiveButtonTitle"
      class="!w-7 !h-7 !p-0 !min-w-0"
      size="small"
      severity="secondary"
      outlined
      @click="toggle"
    >
      <template #icon-start>
        <Sparkles :size="16" />
      </template>
    </AppButton>

    <Menu ref="menuRef" :model="[]" :popup="true" class="ai-menu">
      <template #start>
        <div class="p-4 w-80">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-sm font-medium text-surface-900 dark:text-surface-0">{{ t('ai.assistant') }}</h3>
          </div>

          <div class="mb-3">
            <AppSelectButton
              v-model="activeTab"
              :options="tabOptions"
              optionLabel="name"
              optionValue="value"
              size="small"
              class="w-full"
            />
          </div>

          <div v-if="activeTab === 'generate'" class="space-y-3">
            <div>
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">
                {{ t('ai.labels.description') }}
              </label>
              <AppTextarea
                v-model="prompt"
                :placeholder="t('ai.placeholders.content_description')"
                class="w-full"
                :rows="3"
                :autoResize="false"
                size="small"
              />
            </div>

            <div class="flex gap-2">
              <AppButton
                type="button"
                :label="isGenerating ? t('ai.buttons.stop') : t('ai.buttons.generate')"
                :disabled="!isGenerating && !prompt.trim()"
                :loading="isGenerating"
                size="small"
                :severity="isGenerating ? 'danger' : 'primary'"
                @click="isGenerating ? stop() : generate()"
              />
              <AppButton
                v-if="result"
                type="button"
                :label="t('ai.buttons.insert')"
                size="small"
                severity="secondary"
                outlined
                @click="insertGenerated"
              />
            </div>

            <div v-if="result || isGenerating">
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">
                {{ t('ai.labels.generated_result') }}
              </label>
              <div
                class="result-container w-full px-3 py-2 text-xs rounded-md min-h-[60px] max-h-[120px] overflow-y-auto"
              >
                <div v-if="isGenerating" class="flex items-center space-x-2">
                  <div
                    class="animate-spin w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full"
                  ></div>
                  <span class="text-surface-500 dark:text-surface-400">{{ t('ai.buttons.generating') }}</span>
                </div>
                <p
                  v-else-if="result"
                  class="whitespace-pre-wrap text-surface-900 dark:text-surface-0"
                >
                  {{ result }}
                </p>
              </div>
            </div>
          </div>

          <div v-else class="space-y-3">
            <div>
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">
                {{ t('ai.labels.target_language') }}
              </label>
              <AppSelect
                v-model="translateTargetLanguage"
                :options="languageOptions"
                optionLabel="name"
                optionValue="value"
                :placeholder="t('ai.placeholders.select_language')"
                class="w-full"
                size="small"
              />
            </div>

            <div>
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">
                {{ t('ai.labels.text_to_translate') }}
              </label>
              <AppTextarea
                v-model="translateSource"
                :placeholder="t('ai.placeholders.text_to_translate_long')"
                class="w-full"
                :rows="3"
                :autoResize="false"
                size="small"
              />
            </div>

            <div class="flex gap-2">
              <AppButton
                type="button"
                :label="isTranslating ? t('ai.buttons.translating') : t('ai.buttons.translate')"
                :disabled="isTranslating || !translateSource.trim()"
                :loading="isTranslating"
                size="small"
                severity="primary"
                class="flex-1"
                @click="translate"
              />
              <AppButton
                v-if="translateResult"
                type="button"
                :label="t('ai.buttons.insert')"
                size="small"
                severity="secondary"
                outlined
                @click="insertTranslated"
              />
            </div>

            <div v-if="translateResult || isTranslating">
              <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">
                {{ t('ai.labels.translation') }}
              </label>
              <div
                class="result-container w-full px-3 py-2 text-xs rounded-md min-h-[60px] max-h-[120px] overflow-y-auto"
              >
                <div v-if="isTranslating" class="flex items-center space-x-2">
                  <div
                    class="animate-spin w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full"
                  ></div>
                  <span class="text-surface-500 dark:text-surface-400">{{ t('ai.buttons.translating') }}</span>
                </div>
                <p
                  v-else-if="translateResult"
                  class="whitespace-pre-wrap text-surface-900 dark:text-surface-0"
                >
                  {{ translateResult }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Menu>
  </div>

  <!-- Modo embebido (sin botón, contenido directo) -->
  <div v-else class="p-4 w-80">
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-sm font-medium text-surface-900 dark:text-surface-0">{{ t('ai.assistant') }}</h3>
    </div>

    <div class="mb-3">
      <AppSelectButton
        v-model="activeTab"
        :options="tabOptions"
        optionLabel="name"
        optionValue="value"
        size="small"
        class="w-full"
      />
    </div>

    <div v-if="activeTab === 'generate'" class="space-y-3">
      <div>
        <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">
          {{ t('ai.labels.description') }}
        </label>
        <AppTextarea
          v-model="prompt"
          :placeholder="t('ai.placeholders.content_description')"
          class="w-full"
          :rows="3"
          :autoResize="false"
          size="small"
        />
      </div>

      <div class="flex gap-2">
        <AppButton
          type="button"
          :label="isGenerating ? t('ai.buttons.stop') : t('ai.buttons.generate')"
          :disabled="!isGenerating && !prompt.trim()"
          :loading="isGenerating"
          size="small"
          :severity="isGenerating ? 'danger' : 'primary'"
          class="flex-1"
          @click="isGenerating ? stop() : generate()"
        />
        <AppButton
          v-if="result"
          type="button"
          :label="t('ai.buttons.insert')"
          size="small"
          severity="secondary"
          outlined
          @click="insertGenerated"
        />
      </div>

      <div v-if="result || isGenerating">
        <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">
          {{ t('ai.labels.generated_result') }}
        </label>
        <div
          class="result-container w-full px-3 py-2 text-xs rounded-md min-h-[60px] max-h-[120px] overflow-y-auto"
        >
          <div v-if="isGenerating" class="flex items-center space-x-2">
            <div
              class="animate-spin w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full"
            ></div>
            <span class="text-surface-500 dark:text-surface-400">{{ t('ai.buttons.generating') }}</span>
          </div>
          <p v-else-if="result" class="whitespace-pre-wrap text-surface-900 dark:text-surface-0">
            {{ result }}
          </p>
        </div>
      </div>
    </div>

    <div v-else class="space-y-3">
      <div>
        <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">
          {{ t('ai.labels.target_language') }}
        </label>
        <AppSelect
          v-model="translateTargetLanguage"
          :options="languageOptions"
          optionLabel="name"
          optionValue="value"
          :placeholder="t('ai.placeholders.select_language')"
          class="w-full"
          size="small"
        />
      </div>

      <div>
        <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">
          {{ t('ai.labels.text_to_translate') }}
        </label>
        <AppTextarea
          v-model="translateSource"
          :placeholder="t('ai.placeholders.text_to_translate_long')"
          class="w-full"
          :rows="3"
          :autoResize="false"
          size="small"
        />
      </div>

      <div class="flex gap-2">
        <AppButton
          type="button"
          :label="isTranslating ? t('ai.buttons.translating') : t('ai.buttons.translate')"
          :disabled="isTranslating || !translateSource.trim()"
          :loading="isTranslating"
          size="small"
          severity="primary"
          class="flex-1"
          @click="translate"
        />
        <AppButton
          v-if="translateResult"
          type="button"
          :label="t('ai.buttons.insert')"
          size="small"
          severity="secondary"
          outlined
          @click="insertTranslated"
        />
      </div>

      <div v-if="translateResult || isTranslating">
        <label class="block text-xs font-medium text-surface-700 dark:text-surface-300 mb-1">
          {{ t('ai.labels.translation') }}
        </label>
        <div
          class="result-container w-full px-3 py-2 text-xs rounded-md min-h-[60px] max-h-[120px] overflow-y-auto"
        >
          <div v-if="isTranslating" class="flex items-center space-x-2">
            <div
              class="animate-spin w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full"
            ></div>
            <span class="text-surface-500 dark:text-surface-400">{{ t('ai.buttons.translating') }}</span>
          </div>
          <p
            v-else-if="translateResult"
            class="whitespace-pre-wrap text-surface-900 dark:text-surface-0"
          >
            {{ translateResult }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.ai-menu .p-menu) {
  min-width: 320px;
  max-width: 320px;
  border: none;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(8px);
  background: rgba(255, 255, 255, 0.95);
}

.dark :deep(.ai-menu .p-menu) {
  background: rgba(17, 24, 39, 0.95);
}

:deep(.ai-menu .p-menu-list) {
  padding: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.5);
  border-radius: 2px;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(75, 85, 99, 0.5);
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.7);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(75, 85, 99, 0.7);
}

.result-container {
  background: rgb(var(--surface-50));
}

.dark .result-container {
  background: rgb(var(--surface-800));
}
</style>
